import {
    applyContractMergeFields,
    groupContractSections,
    parseContractDoc,
    type ContractBlock,
    type ContractSection
} from "@taskhealth/shared";
import { useMemo, useState } from "react";
import {
    LayoutAnimation,
    Platform,
    Pressable,
    StyleSheet,
    Text,
    UIManager,
    View
} from "react-native";

import { useAuth } from "@/lib/auth";
import { colors, radius, spacing } from "@/lib/theme";

/** Matches CONTRACT_COMPANY in the API — shown so the reader is not blank. */
const COMPANY_ADDRESS = "2329 Nostrand Avenue, Suite 100, Room 118, Brooklyn, NY 11210, US";

/** MM/DD/YYYY, the format the agreement's own tables use. */
function formatToday(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`;
}

/**
 * Long-form legal text, made readable on a phone.
 *
 * The RN contract is roughly forty thousand characters — fifteen numbered
 * sections plus four schedules. Rendered the way every other `plain_text` item
 * is, as one flat string, it is an unbroken wall the nurse scrolls past without
 * reading, and a nurse who cannot find Section 8 has not meaningfully been shown
 * the payment terms.
 *
 * So sections collapse. The parties table and preamble stay open, every numbered
 * section starts closed with its heading as a tap target, and clause labels keep
 * the bold lead-in that makes the structure scannable. Nothing is hidden that she
 * cannot open, and "Expand all" opens the lot for anyone who would rather scroll.
 *
 * The block list comes from the shared parser, the same one the signed PDF walks,
 * so what she reads here and what gets filed under her signature cannot drift.
 */

if (Platform.OS === "android" && UIManager.setLayoutAnimationEnabledExperimental != null) {
    UIManager.setLayoutAnimationEnabledExperimental(true);
}

/** A document is anything authored as markdown — detected, not configured. */
export function looksLikeContractMarkdown(text: string | null | undefined): boolean {
    if (!text) {
        return false;
    }
    return /^#\s+\S/m.test(text);
}

function Runs({
    block,
    size
}: {
    block: Extract<ContractBlock, { kind: "paragraph" | "bullet" }>;
    size: number;
}) {
    return (
        <Text style={[styles.body, { fontSize: size }]}>
            {block.runs.map((run, i) => (
                <Text key={i} style={run.bold ? styles.bold : undefined}>
                    {run.text}
                </Text>
            ))}
        </Text>
    );
}

function Block({ block }: { block: ContractBlock }) {
    switch (block.kind) {
        case "title":
            return <Text style={styles.title}>{block.text}</Text>;
        case "subtitle":
            return <Text style={styles.subtitle}>{block.text}</Text>;
        case "heading":
            // Headings become section buttons; a stray one renders inline.
            return <Text style={styles.inlineHeading}>{block.text}</Text>;
        case "paragraph":
            return (
                <View style={styles.paraWrap}>
                    <Runs block={block} size={13} />
                </View>
            );
        case "bullet":
            return (
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletDot}>•</Text>
                    <View style={styles.bulletBody}>
                        <Runs block={block} size={13} />
                    </View>
                </View>
            );
        case "note":
            return <Text style={styles.note}>{block.text}</Text>;
        case "table":
            return (
                <View style={styles.table}>
                    {block.rows.map((row, i) => (
                        <View key={i} style={[styles.tableRow, i === 0 && styles.tableRowFirst]}>
                            <Text style={styles.tableLabel}>{row.label}</Text>
                            <Text style={styles.tableValue}>{row.value}</Text>
                        </View>
                    ))}
                </View>
            );
    }
}

function SectionCard({
    section,
    open,
    onToggle
}: {
    section: ContractSection;
    open: boolean;
    onToggle: () => void;
}) {
    return (
        <View style={styles.section}>
            <Pressable
                onPress={onToggle}
                accessibilityRole="button"
                accessibilityState={{ expanded: open }}
                style={({ pressed }) => [styles.sectionHeader, pressed && styles.pressed]}
            >
                <Text style={styles.sectionTitle}>{section.heading}</Text>
                <Text style={styles.chevron}>{open ? "−" : "+"}</Text>
            </Pressable>
            {open ? (
                <View style={styles.sectionBody}>
                    {section.blocks.map((block, i) => (
                        <Block key={i} block={block} />
                    ))}
                </View>
            ) : null}
        </View>
    );
}

export function ContractDocument({ markdown }: { markdown: string }) {
    const { user } = useAuth();

    const sections = useMemo(() => {
        // Merge before parsing, exactly as the PDF does. Without this the nurse
        // reads "{{RN_LEGAL_NAME}}" and "{{EFFECTIVE_DATE}}" on the page she is
        // being asked to sign, which looks broken and tells her nothing.
        //
        // The app knows her name and the date. It does NOT know her licence
        // number here (that answer lives on another section's item), and the
        // executed copy is the PDF — so anything unknown renders as a blank
        // rule, never as a raw token.
        const merged = applyContractMergeFields(markdown, {
            RN_LEGAL_NAME:
                [user?.firstName, user?.lastName].filter(Boolean).join(" ") ||
                user?.name ||
                undefined,
            EFFECTIVE_DATE: formatToday(),
            SIGNED_DATE: formatToday(),
            COMPANY_ADDRESS: COMPANY_ADDRESS
        });
        return groupContractSections(parseContractDoc(merged));
    }, [markdown, user]);
    const [openIds, setOpenIds] = useState<Set<number>>(new Set());

    const collapsible = sections.filter((s) => s.heading !== null);
    const allOpen = collapsible.length > 0 && openIds.size === collapsible.length;

    const toggle = (index: number): void => {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const toggleAll = (): void => {
        LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
        setOpenIds(
            allOpen
                ? new Set()
                : new Set(
                      sections.map((s, i) => (s.heading !== null ? i : -1)).filter((i) => i >= 0)
                  )
        );
    };

    return (
        <View style={styles.root}>
            {sections.map((section, index) =>
                section.heading === null ? (
                    <View key={index} style={styles.preamble}>
                        {section.blocks.map((block, i) => (
                            <Block key={i} block={block} />
                        ))}
                    </View>
                ) : null
            )}

            {collapsible.length > 0 ? (
                <Pressable
                    onPress={toggleAll}
                    accessibilityRole="button"
                    style={({ pressed }) => [styles.expandAll, pressed && styles.pressed]}
                >
                    <Text style={styles.expandAllText}>
                        {allOpen ? "Collapse all sections" : "Expand all sections"}
                    </Text>
                </Pressable>
            ) : null}

            {sections.map((section, index) =>
                section.heading !== null ? (
                    <SectionCard
                        key={index}
                        section={section}
                        open={openIds.has(index)}
                        onToggle={() => toggle(index)}
                    />
                ) : null
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    root: { gap: spacing.sm },
    preamble: {
        backgroundColor: colors.backgroundSecondary,
        borderRadius: radius.md,
        padding: spacing.md,
        gap: 2
    },
    title: {
        fontFamily: "PlusJakartaSans_700Bold",
        fontSize: 17,
        lineHeight: 23,
        color: colors.ink,
        textAlign: "center",
        marginBottom: 2
    },
    subtitle: {
        fontFamily: "PlusJakartaSans_600SemiBold",
        fontSize: 13,
        lineHeight: 18,
        color: colors.textSecondary,
        textAlign: "center",
        marginBottom: spacing.xs
    },
    inlineHeading: {
        fontFamily: "PlusJakartaSans_700Bold",
        fontSize: 14,
        color: colors.ink,
        marginTop: spacing.sm,
        marginBottom: 4
    },
    body: {
        fontFamily: "PlusJakartaSans_400Regular",
        lineHeight: 19,
        color: colors.textPrimary
    },
    bold: { fontFamily: "PlusJakartaSans_700Bold", color: colors.ink },
    paraWrap: { marginBottom: 8 },
    bulletRow: { flexDirection: "row", gap: 8, marginBottom: 6, paddingRight: 4 },
    bulletDot: { color: colors.teal600, fontSize: 13, lineHeight: 19 },
    bulletBody: { flex: 1 },
    note: {
        fontFamily: "PlusJakartaSans_400Regular",
        fontStyle: "italic",
        fontSize: 12.5,
        lineHeight: 18,
        color: colors.textSecondary,
        marginTop: 4
    },
    table: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.sm,
        overflow: "hidden",
        marginVertical: spacing.xs,
        backgroundColor: colors.surface
    },
    tableRow: {
        flexDirection: "row",
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingVertical: 8,
        paddingHorizontal: 10,
        gap: 10
    },
    tableRowFirst: { borderTopWidth: 0, backgroundColor: colors.backgroundSecondary },
    tableLabel: {
        flex: 1,
        fontFamily: "PlusJakartaSans_600SemiBold",
        fontSize: 12,
        lineHeight: 17,
        color: colors.textSecondary
    },
    tableValue: {
        flex: 1.3,
        fontFamily: "PlusJakartaSans_400Regular",
        fontSize: 12,
        lineHeight: 17,
        color: colors.textPrimary
    },
    expandAll: { alignSelf: "flex-start", paddingVertical: 6, paddingHorizontal: 2 },
    expandAllText: {
        fontFamily: "PlusJakartaSans_600SemiBold",
        fontSize: 12.5,
        color: colors.teal600
    },
    section: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.md,
        backgroundColor: colors.surface,
        overflow: "hidden"
    },
    sectionHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        paddingVertical: 12,
        paddingHorizontal: spacing.md
    },
    sectionTitle: {
        flex: 1,
        fontFamily: "PlusJakartaSans_600SemiBold",
        fontSize: 13,
        lineHeight: 18,
        color: colors.ink
    },
    chevron: {
        fontFamily: "PlusJakartaSans_700Bold",
        fontSize: 17,
        color: colors.teal600,
        width: 16,
        textAlign: "center"
    },
    sectionBody: {
        paddingHorizontal: spacing.md,
        paddingBottom: spacing.md,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: spacing.sm
    },
    pressed: { opacity: 0.6 }
});
